import React, { useState } from "react";
import { Mail, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";

const DISMISS_KEY = "argus.verify-banner.dismissed-until";

export default function VerifyBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Number.isFinite(t) && t > Date.now();
  });

  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    if (sending) return;
    setSending(true);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      toast.success("Verification email sent. Check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend right now. Try again in a minute.");
    } finally {
      setSending(false);
    }
  };

  const dismiss = () => {
    // 24-hour dismiss
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setDismissed(true);
  };

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 bg-gold/10 border-b border-gold/30 text-sm"
      data-testid="verify-banner"
    >
      <Mail size={16} className="text-gold shrink-0" />
      <p className="flex-1 text-ink/90">
        <strong className="text-ink">Verify your email.</strong>{" "}
        We sent a verification link to <span className="font-mono text-ink">{user.email}</span>. Some features stay locked until you confirm.
      </p>
      <Button
        size="sm"
        onClick={resend}
        disabled={sending}
        className="bg-gold text-paper hover:bg-gold2 h-8 font-semibold text-xs"
        data-testid="verify-banner-resend"
      >
        {sending ? <Loader2 size={12} className="animate-spin" /> : "Resend email"}
      </Button>
      <button
        onClick={dismiss}
        aria-label="Dismiss for 24 hours"
        className="text-ink/40 hover:text-ink p-1"
        data-testid="verify-banner-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
